/**
 * =============================================================================
 * KpiCard — Card horizontal compacto para métricas de listagem.
 *
 * Replica fielmente o padrão visual usado em FinancialCharges/ReferralPayouts/
 * ReferralCommissions: ícone + label à esquerda, valor em destaque à direita,
 * fundo colorido translúcido por categoria semântica.
 *
 * Cores suportadas: purple, green, blue, cyan, yellow, red, lime, orange, slate.
 * Dark mode automático (variante `-900/30`).
 *
 * @version 1.1.0
 * RELEASE: 2026-10-16 v1.1.0 - Cor "slate" (mesma paleta do card "Pendentes" da
 *            listagem de Tarefas) e prop `activeClassName`, que troca o anel
 *            padrão do card ativo por um destaque próprio (ex.: card "Total
 *            Remessa", que não deve ficar com borda de seleção).
 * RELEASE: 2026-04-25 v1.0.0 - Criação do helper de KPI card padronizado.
 * =============================================================================
 */
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/** Cores semânticas suportadas. */
export type KpiCardColor =
  | 'purple'
  | 'green'
  | 'blue'
  | 'cyan'
  | 'yellow'
  | 'red'
  | 'lime'
  | 'orange'
  | 'slate';

/** Mapa cor → classe de fundo (claro + dark). */
const BG_BY_COLOR: Record<KpiCardColor, string> = {
  purple: 'bg-purple-100 dark:bg-purple-900/30',
  green:  'bg-green-100 dark:bg-green-900/30',
  blue:   'bg-blue-100 dark:bg-blue-900/30',
  cyan:   'bg-cyan-100 dark:bg-cyan-900/30',
  yellow: 'bg-yellow-100 dark:bg-yellow-900/30',
  red:    'bg-red-100 dark:bg-red-900/30',
  lime:   'bg-lime-100 dark:bg-green-900/30',
  orange: 'bg-orange-100 dark:bg-orange-900/30',
  slate:  'bg-slate-100 dark:bg-slate-900/30',
};

/** Mapa cor → classe do ícone. */
const ICON_BY_COLOR: Record<KpiCardColor, string> = {
  purple: 'text-purple-700 dark:text-purple-300',
  green:  'text-green-700 dark:text-green-300',
  blue:   'text-blue-700 dark:text-blue-300',
  cyan:   'text-cyan-700 dark:text-cyan-300',
  yellow: 'text-yellow-700 dark:text-yellow-300',
  red:    'text-red-700 dark:text-red-300',
  lime:   'text-lime-700 dark:text-lime-300',
  orange: 'text-orange-700 dark:text-orange-300',
  slate:  'text-slate-700 dark:text-slate-300',
};

interface KpiCardProps {
  /** Cor semântica do card. */
  color: KpiCardColor;
  /** Ícone (lucide-react). */
  icon: LucideIcon;
  /** Rótulo curto (ex.: "Total Cobranças"). */
  label: string;
  /** Valor principal (string ou número formatado). */
  value: React.ReactNode;
  /** Sufixo opcional em fonte menor (ex.: "pagas"). */
  suffix?: string;
  /** Classes adicionais. */
  className?: string;
  /** Quando definido, o card vira clicável (cursor pointer + hover). */
  onClick?: () => void;
  /** Marca o card como ativo (filtro selecionado). */
  active?: boolean;
  /**
   * Destaque do card ativo que substitui o anel padrão (ex.: "Total Remessa",
   * que representa a visão sem filtro e não deve ganhar borda de seleção).
   */
  activeClassName?: string;
}

/** Mapa cor → classe de anel quando ativo. */
const RING_BY_COLOR: Record<KpiCardColor, string> = {
  purple: 'ring-2 ring-purple-500',
  green:  'ring-2 ring-green-500',
  blue:   'ring-2 ring-blue-500',
  cyan:   'ring-2 ring-cyan-500',
  yellow: 'ring-2 ring-yellow-500',
  red:    'ring-2 ring-red-500',
  lime:   'ring-2 ring-lime-500',
  orange: 'ring-2 ring-orange-500',
  slate:  'ring-2 ring-slate-500',
};

/** Card de KPI horizontal compacto. */
export function KpiCard({
  color,
  icon: Icon,
  label,
  value,
  suffix,
  className,
  onClick,
  active,
  activeClassName,
}: KpiCardProps) {
  const clickable = typeof onClick === 'function';
  return (
    <Card
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        'border-0 shadow-none transition-all hover:shadow-md',
        BG_BY_COLOR[color],
        clickable && 'cursor-pointer hover:scale-[1.01]',
        active && (activeClassName ?? RING_BY_COLOR[color]),
        className,
      )}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          {/* Ícone + label */}
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', ICON_BY_COLOR[color])} />
            <p className="text-xs font-medium truncate">{label}</p>
          </div>
          {/* Valor + sufixo */}
          <p className="text-xl font-bold leading-tight shrink-0">
            {value}
            {suffix && (
              <span className="text-[10px] font-normal text-muted-foreground ml-1">
                {suffix}
              </span>
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
